import React from 'react';
import { Instagram, Linkedin, ExternalLink, Users, Award, Sparkles } from 'lucide-react';

// Custom TikTok Icon
const TikTokIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SocialFeed = () => {
  const socialPlatforms = [
    {
      name: 'TikTok',
      handle: '@corporate.awards',
      url: 'https://www.tiktok.com/@corporate.awards',
      icon: TikTokIcon,
      description: 'Behind-the-scenes looks at custom awards and engraving',
      gradient: 'from-gray-900 to-gray-800',
      hoverGradient: 'group-hover:from-pink-500 group-hover:via-red-500 group-hover:to-yellow-500',
      stat: 'Videos',
      statIcon: Sparkles,
    },
    {
      name: 'LinkedIn',
      handle: 'Corporate Awards & Graphics',
      url: 'https://www.linkedin.com/company/corporate-awards-graphics/',
      icon: Linkedin,
      description: 'Industry insights and corporate recognition trends',
      gradient: 'from-blue-700 to-blue-600',
      hoverGradient: 'group-hover:from-blue-600 group-hover:to-blue-500',
      stat: 'Connect',
      statIcon: Users,
    },
    {
      name: 'Instagram',
      handle: '@engrave.ca',
      url: 'https://www.instagram.com/engrave.ca/',
      icon: Instagram,
      description: 'Stunning award designs and customer spotlights',
      gradient: 'from-purple-600 via-pink-500 to-orange-400',
      hoverGradient: 'group-hover:from-purple-500 group-hover:via-pink-400 group-hover:to-orange-300',
      stat: 'Gallery',
      statIcon: Award,
    },
  ];

  return (
    <section className="py-20 bg-ivory-50" data-testid="social-feed-section">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-gold-500/10 text-gold-700 text-sm font-medium rounded-full mb-4 font-body">
            Stay Connected
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-ivory-800 mb-4">
            Follow Us on Social
          </h2>
          <p className="text-ivory-500 font-body max-w-2xl mx-auto">
            Get inspired by our latest projects, behind-the-scenes content, and exclusive offers. 
            Join our community of businesses who celebrate excellence.
          </p>
        </div>

        {/* Social Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {socialPlatforms.map((platform) => (
            <a
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden rounded-2xl bg-ivory-100 border border-ivory-300/50 shadow-luxury hover:shadow-luxury-lg transition-all duration-500 hover:-translate-y-1"
              data-testid={`social-card-${platform.name.toLowerCase()}`}
            >
              {/* Gradient Header */}
              <div className={`h-24 bg-gradient-to-r ${platform.gradient} ${platform.hoverGradient} transition-all duration-500 relative overflow-hidden`}>
                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />
                
                {/* Platform Icon */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <platform.icon className="w-10 h-10 text-white/90" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-ivory-800 group-hover:text-gold-700 transition-colors">
                      {platform.name}
                    </h3>
                    <p className="text-gold-600 text-sm font-body font-medium">
                      {platform.handle}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-ivory-400 group-hover:text-gold-600 transition-colors" />
                </div>
                
                <p className="text-ivory-500 text-sm font-body mb-4 leading-relaxed">
                  {platform.description}
                </p>

                {/* CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-ivory-200">
                  <div className="flex items-center gap-2 text-ivory-500">
                    <platform.statIcon className="w-4 h-4" />
                    <span className="text-sm font-body">{platform.stat}</span>
                  </div>
                  <span className="text-sm font-body font-medium text-gold-600 group-hover:text-gold-700 transition-colors flex items-center gap-1">
                    Follow
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-10">
          <p className="text-ivory-400 text-sm font-body">
            Tag us in your photos with <span className="text-gold-600 font-medium">#Engraveca</span> for a chance to be featured
          </p>
        </div>
      </div>
    </section>
  );
};

export default SocialFeed;
